import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories, videos } from "@/mocks/videos";
import { totalViews } from "@/lib/videos";

const scopeItems = [
  {
    icon: "ri-football-line",
    title: "赛事类型覆盖",
    text: "足球、篮球、电竞、赛车与综合赛事五大类，每个类别按每日更新单独归档，方便横向对比同类型失误。",
  },
  {
    icon: "ri-time-line",
    title: "时间维度归档",
    text: "条目按本周、本月与经典回顾三个时间段整理，既能追当日赛况，也能回看跨赛季的经典片段。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选检索",
    text: "分类页支持时间段、比赛等级与地区组合筛选，并可切换按上传时间或热度排序。",
  },
  {
    icon: "ri-search-line",
    title: "标题与标签搜索",
    text: "搜索覆盖视频标题、赛事标签与赛事类型，结果支持分页，便于在大量条目中快速定位。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于本站 - 51视频每日大赛今日黑榜合集",
    "了解 51视频每日大赛今日黑榜合集的站点定位、内容收录范围、更新机制与联系方式，以及内容免责声明。",
  );

  return (
    <PageShell>
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
            <div>
              <p className="font-label text-[11px] tracking-[0.28em] text-secondary-600">ABOUT</p>
              <h1 className="mt-2 text-3xl font-bold leading-tight text-foreground-950 md:text-[38px]">
                关于 51视频每日大赛
                <br className="hidden md:block" />
                今日黑榜合集
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                我们是一个专注于每日赛事“黑榜”视频的汇总站点。不同于只剪高光的集锦，我们把注意力放在比赛里那些直接影响结果的失误瞬间，
                按赛事类型与时间归档，让观众可以系统地回看、对比与讨论。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: `${videos.length}`, label: "收录条目" },
                { value: `${categories.length}`, label: "赛事类别" },
                { value: totalViews(), label: "累计观看" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-3 py-4 text-center">
                  <p className="font-label text-xl font-semibold text-primary-700">{item.value}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-page py-12 md:py-16" aria-labelledby="scope-title">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="scope-title" className="text-2xl font-bold text-foreground-950">内容收录范围</h2>
            <p className="mt-2 text-sm text-foreground-600">只收录与赛事结果直接相关的关键片段，不做无意义的情绪化剪辑。</p>
          </div>
          <Link to="/search" className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary-700 hover:text-primary-800">
            浏览全部条目
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {scopeItems.map((item) => (
            <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${item.icon} text-lg`} />
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16" aria-labelledby="editorial-rule-title">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div>
            <h2 id="editorial-rule-title" className="text-2xl font-bold text-foreground-950">更新与收录规则</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              站点模拟每日 08:00 更新昨日赛事的黑榜条目。每条视频都需要满足至少一个收录条件：直接影响比分、
              包含可复盘的战术细节、或具备规则讨论价值。我们不会为了凑数收录无关片段。
            </p>
            <div className="mt-5 space-y-4">
              {[
                {
                  step: "01",
                  title: "赛事筛选",
                  text: "从当日结束的赛事中挑选出具有讨论价值的失误片段，优先保留多机位可回看的镜头。",
                },
                {
                  step: "02",
                  title: "分类归档",
                  text: "按赛事类型、比赛等级与地区打标签，并归入本周、本月或经典回顾，方便后续组合筛选。",
                },
                {
                  step: "03",
                  title: "笔记补充",
                  text: "为每条视频补写赛事笔记，说明发生时间、技术原因与后续影响，让回看不止是看热闹。",
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="font-label text-xl font-semibold text-background-400">{item.step}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-1 text-[13px] leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-bold text-foreground-950">联系方式</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                内容纠错、分类建议或合作咨询，欢迎通过邮件联系。这是一个演示邮箱，仅用于展示页面结构。
              </p>
              <a
                href="mailto:contact@51daily-blame.example"
                className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-mail-line text-base text-primary-600" />
                contact@51daily-blame.example
              </a>
              <p className="mt-3 text-xs text-foreground-500">一般会在 2 个工作日内回复（演示说明）。</p>
            </div>

            <div className="rounded-lg border border-primary-100 bg-primary-50 p-5">
              <h2 className="text-base font-bold text-primary-800">播放说明</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-primary-700">
                站内所有播放区域均为前端模拟，点击后会展示加载与播放状态效果，
                <strong className="font-semibold">真实视频播放接口待接入</strong>，目前不提供任何赛事直播或回放下载。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-bold text-foreground-950">快速导航</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Link to="/" className="rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-secondary-100">
                  首页
                </Link>
                <Link to="/search" className="rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-secondary-100">
                  全部视频
                </Link>
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-secondary-100"
                  >
                    {category.shortName}黑榜
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="container-page py-12 md:py-16" aria-labelledby="disclaimer-title">
        <h2 id="disclaimer-title" className="text-2xl font-bold text-foreground-950">免责声明与版权声明</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-semibold text-foreground-950">免责声明</h3>
            <ul className="mt-3 space-y-2 text-[13px] leading-relaxed text-foreground-600">
              <li>· 本站为赛事视频汇总展示的演示站点，所有视频数据、封面、播放界面均为演示内容。</li>
              <li>· 站内不提供赛事直播、回放下载或任何形式的付费观看服务。</li>
              <li>· 页面中的浏览量、上传者与期数等数据仅用于展示排序与筛选效果，不代表真实统计。</li>
              <li>· 若未来接入真实内容源，将以合规授权内容为准，并及时更新本说明。</li>
            </ul>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-semibold text-foreground-950">版权声明</h3>
            <ul className="mt-3 space-y-2 text-[13px] leading-relaxed text-foreground-600">
              <li>· 站点名称、栏目结构、文字笔记与页面设计归本站所有。</li>
              <li>· 演示封面图由生成工具产出，仅用于本演示项目，不指向任何真实赛事画面。</li>
              <li>· 转载请注明来源，禁止将本站内容用于商业转售或批量抓取。</li>
              <li>· 如对内容有异议，可通过上方邮箱联系，我们会尽快处理。</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-5 py-4 sm:flex-row sm:items-center">
          <p className="text-xs text-foreground-500">© 2026 51视频每日大赛今日黑榜合集 · 保留所有权利</p>
          <Link to="/" className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary-700 hover:text-primary-800">
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </section>
    </PageShell>
  );
}